/* eslint-disable tailwindcss/no-custom-classname */
/* eslint-disable tailwindcss/no-arbitrary-value */
import React, { useContext } from 'react';
import proptypes from 'prop-types';
const { shape, string, bool, number, arrayOf } = proptypes;
import { useConfigService, ExperienceContext } from '@thd-nucleus/experience-context';
import dayjs from 'dayjs';
import { Typography } from '@one-thd/sui-atomic-components';
import { ShippingThreshold } from './ShippingThreshold';
import {
  formatDate,
  showTodayOrTomorrow,
  showDateOrDateRange,
  formatInventory,
  isMobile
} from '../../../helper/utils';
import { TileHead } from '../TileHead';
import { TileFooter } from '../TileFooter';
import { useAssemblyListeners } from '../../../../hooks/useAssemblyListeners';
import { FULFILLMENT_METHODS } from '../../../constants';
import { IDS } from '../../../../automationTestIds';
import { useThdCustomer } from '@thd-olt-functional/customer-information';

const DefaultShipping = ({
  deliveryStartDate = null,
  deliveryEndDate = null,
  deliveryTimeline = null,
  type = null,
  hasFreeShipping = false,
  totalCharge = null,
  maxInventoryAvail = null,
  backordered = false,
  maxCartPriceContributor = null,
  selected = false,
  isCartTiles = false,
  testID = '',
  isSpecialOrder = false,
  configuratorHideQuantity = false,
  isBodfsGrillsAssemblyEligible = false,
  hideShippingThreshold = false,
  isBlindsItem = false,
  isBIP = false,
  bossQty = 0
}) => {
  const { channel } = useContext(ExperienceContext);
  const isBodfsGrillsAssemblyEnabled = useConfigService('bodfsGrillsAssemblyEnabled');
  const bodfsAssemblyEtaEnabled = useConfigService('bodfsAssemblyEtaEnabled') || false;
  const { isBODFSAssemblySelected } = useAssemblyListeners({
    isBodfsGrillsAssemblyEnabled,
    isBodfsGrillsAssemblyEligible
  });
  const isDeliveryWithAssemblySku = type === FULFILLMENT_METHODS.EXPRESS_DELIVERY && isBODFSAssemblySelected;
  // for adding 2 days to deliveries that are BODFS and have pre assembly selected
  const deliveryStartDateAdj = bodfsAssemblyEtaEnabled && isDeliveryWithAssemblySku
    ? dayjs(deliveryStartDate).add(2, 'day').format('YYYY-MM-DD')
    : deliveryStartDate;
  const deliveryEndDateAdj = bodfsAssemblyEtaEnabled && isDeliveryWithAssemblySku
    ? dayjs(deliveryEndDate).add(2, 'day').format('YYYY-MM-DD')
    : deliveryEndDate;

  const formattedStartDate = isBIP ? formatDate(deliveryEndDateAdj) : formatDate(deliveryStartDateAdj);
  const formattedEndDate = formatDate(deliveryEndDateAdj);
  const formattedInventoryQty = formatInventory(maxInventoryAvail);

  const showDeliveryStartDate = deliveryStartDateAdj || deliveryEndDateAdj;

  const { isExchangeCustomer } = useThdCustomer() || {};
  const isAPOFPOZipCode = window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.isAPOFPOZipCode?.() || false;
  const shouldHideDeliveryDate = isAPOFPOZipCode && isExchangeCustomer;

  const renderDeliveryStartDate = () => {
    if ((!bodfsAssemblyEtaEnabled && type === FULFILLMENT_METHODS.EXPRESS_DELIVERY && isBODFSAssemblySelected)
      || !showDeliveryStartDate) {
      return (
        <div
          className="sui-font-bold sui-min-h-[24px] sui-pb-1"
          data-automation-id={IDS.ETA.sthETA}
        >
          {"\u00A0"}
        </div>
      );
    }

    if (formattedStartDate === formattedEndDate) {
      const timelineAdj = isBODFSAssemblySelected && isDeliveryWithAssemblySku ? 'later' : deliveryTimeline;
      return (
        <div
          className="sui-font-bold sui-min-h-[24px] sui-pb-1"
          data-automation-id={IDS.ETA.sthETA}
        >
          {showTodayOrTomorrow(deliveryEndDateAdj, true, timelineAdj, formattedEndDate)}
        </div>
      );
    }

    return (
      <div className="sui-font-bold sui-min-h-[24px] sui-pb-1" data-automation-id={IDS.ETA.sthETA}>
        {showDateOrDateRange({ formattedStartDate, formattedEndDate })}
      </div>
    );
  };

  return (
    <div className="" data-testid={testID}>
      <TileHead
        title="Delivery"
        automationId={IDS.LABEL.sthLabel}
      />
      <div className="delivery-tile-content">
        <div data-testid="ffm-details-row" className="sui-pt-2">
          {!shouldHideDeliveryDate && (
            <Typography component="div" variant="body-base" weight="bold">
              {renderDeliveryStartDate()}
            </Typography>
          )}
          <Typography component="div" variant="body-base">
            {backordered && type === FULFILLMENT_METHODS.STH_FFM ? (
              <div
                data-testid="fulfillment-qty-row"
                className="sui-min-h-[24px]"
                data-automation-id={IDS.QTY.sthQty}
              >
                Backordered
              </div>
            ) : (
              <div
                data-testid="fulfillment-qty-row"
                className={isBlindsItem ? 'sui-min-h-[28px]' : 'sui-min-h-[24px]'}
                data-automation-id={IDS.QTY.sthQty}
              >
                {!!formattedInventoryQty && (!isSpecialOrder && !configuratorHideQuantity) && (
                  <div className={isMobile(channel) && bossQty.toString().length > 4 ? 'sui-text-sm' : ''}>
                    <span className="sui-text-success">{formattedInventoryQty}</span> available
                  </div>
                )}
              </div>
            )}
          </Typography>

        </div>
        <TileFooter className="sui-font-bold" isDelivery>
          {!hideShippingThreshold
            && (
              <ShippingThreshold
                type={type}
                hasFreeShipping={hasFreeShipping}
                totalCharge={totalCharge}
                maxCartPriceContributor={maxCartPriceContributor}
                selected={selected}
                isCartTiles={isCartTiles}
              />
            )}
          {hideShippingThreshold && <span className="sui-pt-2 sui-min-h-[24px]">{"\u00A0"}</span>}
        </TileFooter>

      </div>
    </div>
  );
};

DefaultShipping.propTypes = {
  deliveryStartDate: string,
  deliveryEndDate: string,
  deliveryTimeline: string,
  type: string,
  hasFreeShipping: bool,
  totalCharge: number,
  maxInventoryAvail: number,
  backordered: bool,
  maxCartPriceContributor: string,
  selected: bool,
  isCartTiles: bool,
  testID: string,
  isSpecialOrder: bool,
  configuratorHideQuantity: bool,
  isBodfsGrillsAssemblyEligible: bool,
  hideShippingThreshold: bool,
  isBlindsItem: bool,
  isBIP: bool,
  bossQty: number,
};

export { DefaultShipping };
